/**
 * Strips markdown formatting from a string
 * @param text - The markdown formatted string to process
 * @returns A plain text string with markdown formatting removed
 */
export const stripMarkdown = (text: string): string => {
  if (!text) return '';

  // First, remove code blocks (including their content)
  text = text.replace(/```[\s\S]*?```/g, '');

  const lines = text.split('\n');
  const resultLines: string[] = [];

  // Process line by line
  // biome-ignore lint/style/useForOf: performance
  for (let i = 0; i < lines.length; i++) {
    let line = lines[i] ?? '';

    // Headers
    line = line.replace(/^#{1,6}\s+/, '');

    // Horizontal rules - skip these lines entirely
    if (/^[\s]*(\*{3,}|-{3,}|_{3,})[\s]*$/.test(line)) {
      resultLines.push(''); // Add an empty line instead
      continue;
    }

    // Blockquotes
    line = line.replace(/^\s*>\s+/, '');

    // Lists
    line = line.replace(/^\s*(\d+\.|\*|\+|-)\s+/, '');

    // Links and images
    line = line.replace(/!?\[([^\]]+)\]\([^)]+\)/g, '$1');

    // Inline code
    line = line.replace(/`([^`]+)`/g, '$1');

    // Bold and italic - handle nested cases first
    // Triple emphasis (bold + italic)
    line = line.replace(/(\*\*\*|___)([^*_]+)(\*\*\*|___)/g, '$2');

    // Bold
    line = line.replace(/(\*\*|__)([^*_]+)(\*\*|__)/g, '$2');

    // Italic
    line = line.replace(/(\*|_)([^*_]+)(\*|_)/g, '$2');

    // HTML tags
    line = line.replace(/<[^>]*>/g, '');

    resultLines.push(line);
  }

  // Join and normalize multiple newlines
  let result = resultLines.join('\n');
  result = result.replace(/\n{3,}/g, '\n\n');

  return result;
};

/**
 * Strips math-related delimiters from text to prepare it for TTS processing,
 * while preserving the actual mathematical notation and symbols including commas
 * @param text The input text containing math notation
 * @returns The text with only math delimiters removed
 */
// TODO: Move this helper to the math package when its consumers can take that dependency.
export const stripMathDelimiters = (text: string): string => {
  // Replace inline math delimiters: $...$ or \(...\)
  let result = text.replace(/\$([^$]*)\$|\\\(([^)]*)\\\)/g, (_, g1, g2) => g1 || g2 || '');

  // Replace display math delimiters: $$...$$ or \[...\]
  result = result.replace(/\$\$([^$]*)\$\$|\\\[([^\]]*)\\\]/g, (_, g1, g2) => g1 || g2 || '');

  // Handle both single and double backslash delimiters for environments
  // Single backslash: \begin{equation}...\end{equation}
  result = result.replace(/\\begin\{[^}]*\}([\s\S]*?)\\end\{[^}]*\}/g, '$1');

  // Double backslash: \\begin{equation}...\\end{equation}
  result = result.replace(/\\\\begin\{[^}]*\}([\s\S]*?)\\\\end\{[^}]*\}/g, '$1');

  // Replace line continuation commands (\\, \\\, etc.) with spaces
  result = result.replace(/\\{2,}/g, ' ');

  // Replace LaTeX spacing commands with appropriate punctuation or spaces
  // Convert \, to a simple comma
  result = result.replace(/\\,/g, ',');

  // Replace other spacing commands with spaces
  result = result.replace(/\\(quad|qquad|hspace|vspace|;|:|!|\s)/g, ' ');

  // Replace specific text formatting commands
  result = result.replace(/\\(text|textbf|textit|textrm|textsf|texttt)\{([^}]*)\}/g, '$2');

  // Replace specific math formatting commands that shouldn't be read
  result = result.replace(/\\(mathbf|mathit|mathrm|mathsf|mathtt|mathcal|mathbb)\{([^}]*)\}/g, '$2');

  // Replace multiple spaces with a single space
  result = result.replace(/\s+/g, ' ').trim();

  return result;
};
